import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { HOTEL } from "@/data/hotel";

// Per-section grid span patterns. Cycled if the section has more images than spans.
const SPAN_PATTERNS = {
  exteriors: [
    "md:col-span-7 h-[320px] md:h-[520px]",
    "md:col-span-5 h-[320px] md:h-[520px]",
  ],
  rooms: [
    "md:col-span-8 h-[340px] md:h-[520px]",
    "md:col-span-4 h-[340px] md:h-[520px]",
    "md:col-span-4 h-[260px] md:h-[360px]",
    "md:col-span-4 h-[260px] md:h-[360px]",
    "md:col-span-4 h-[260px] md:h-[360px]",
    "md:col-span-6 h-[300px] md:h-[420px]",
    "md:col-span-6 h-[300px] md:h-[420px]",
    "md:col-span-5 h-[280px] md:h-[380px]",
    "md:col-span-7 h-[280px] md:h-[380px]",
    "md:col-span-4 h-[260px] md:h-[360px]",
    "md:col-span-4 h-[260px] md:h-[360px]",
    "md:col-span-4 h-[260px] md:h-[360px]",
  ],
  bathrooms: [
    "md:col-span-4 h-[300px] md:h-[440px]",
    "md:col-span-4 h-[300px] md:h-[440px]",
    "md:col-span-4 h-[300px] md:h-[440px]",
  ],
};

const Section = ({ group, index }) => {
  const spans = SPAN_PATTERNS[group.id] || SPAN_PATTERNS.rooms;
  return (
    <section
      data-testid={`gallery-section-${group.id}`}
      className={index === 0 ? "" : "mt-28 md:mt-36"}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex items-end justify-between gap-8 flex-wrap">
            <div>
              <div className="overline">{group.eyebrow}</div>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.02] text-ink-900">
                {group.title}
              </h2>
            </div>
            <p className="text-ink-600 max-w-md md:text-right leading-relaxed">
              {group.blurb}
            </p>
          </div>
          <div className="mt-8 h-px bg-ink-900/10" />
        </Reveal>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-10 grid grid-cols-1 md:grid-cols-12 auto-rows-auto gap-4 md:gap-6">
        {group.images.map((src, i) => (
          <Reveal
            key={`${group.id}-${i}`}
            delay={i * 0.04}
            className={`${spans[i % spans.length]} relative overflow-hidden group bg-paper-200`}
          >
            <img
              src={src}
              alt={`${group.title} — ${i + 1}`}
              loading="lazy"
              decoding="async"
              data-testid={`gallery-${group.id}-${i}`}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
            />
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export default function Gallery() {
  return (
    <div className="pt-32 md:pt-40 pb-24" data-testid="gallery-page">
      <Seo
        title="Gallery — Hotel Azulo Inn, Safdarjung Enclave"
        description="A photographic walk through Hotel Azulo Inn — exteriors, garden, rooms, and bathrooms at our boutique hotel in Safdarjung Enclave, New Delhi."
        path="/gallery"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="overline">Gallery</div>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.98] text-ink-900 max-w-3xl">
            The hotel, in
            <span className="italic text-sage-dark"> quiet frames.</span>
          </h1>
          <p className="mt-6 text-ink-600 max-w-xl leading-relaxed">
            A walk through Hotel Azulo Inn — outside in, room by room.
          </p>
        </Reveal>
      </div>

      <div className="mt-20">
        {HOTEL.galleryGroups.map((group, idx) => (
          <Section key={group.id} group={group} index={idx} />
        ))}
      </div>
    </div>
  );
}
